import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { HISTORY_PAGE_SIZE, NURSES_PAGE_SIZE } from 'src/app/constants/pagination';
import { parametrify } from 'src/app/helpers/parametrify';

import { FacilityModel } from 'src/app/models/facility';
import { FacilityStats } from 'src/app/models/facility/facility-stats.model';
import { PaginationDTO, SortDTO } from 'src/app/types/dto';
import { Name } from 'src/app/types/models/user';
import { environment } from 'src/environments/environment';
import {
  AddSupervisorDTO,
  CreateShiftTemplateDTO,
  RequestChangeDTO,
  InstashyftStatDTO,
  EditShiftTemplateDTO
} from '../../types/dto';
import { FacilityRateChangeHistory } from 'src/app/models/facility/facility-rate-change-history';
import { FacilityNurse } from 'src/app/models/facility/facility-nurse';
import { NursesFiltersDTO } from 'src/app/types/dto/fetch-nurses';
import { catchError, map } from 'rxjs/operators';
import { ManagerSettings } from 'src/app/models/facility/manager-settings';
import { FacilityNurseNotes } from 'src/app/models/facility/facility-nurse-notes';
import { NurseShiftPrefsRequest } from 'src/app/models/facility/nurse-shift-prefs-request';
import { FacilityNurseNotesMixedHistory } from 'src/app/models/facility/facility-nurse-notes-mixed-history';
import { FacilityNurseMixedNotes } from 'src/app/models/facility/facility-nurse-mixed-notes';
import { NursesConfirmedShift } from 'src/app/models/facility/nurses-confirmed-shift';
import { ApplicantEtaInfo } from 'src/app/types/eta';
import { SubscriptionTier } from 'src/app/models/metrics/subscription-tier';
import { TotalSavings } from 'src/app/models/metrics/total-savings';
import { ShiftFillRate } from 'src/app/models/metrics/shift-fill-rate';
import { CancellationRate } from 'src/app/models/metrics/cancellation-rate';
import { YourWorkforce } from 'src/app/models/metrics/your-workforce';
import { AvailableNurses } from 'src/app/models/metrics/available-nurses';

@Injectable({ providedIn: 'root' })
export class FacilitiesService {
  private readonly endpoint = environment.apiUrl;

  constructor(private readonly http: HttpClient) { }

  fetchMyFacilities() {
    return this.http.get<Partial<FacilityModel>[]>(`${this.endpoint}/facilities/assigned`);
  }

  fetchFacility(id: string) {
    return this.http.get<FacilityModel>(`${this.endpoint}/facilities/${id}`);
  }

  edit(id: string, model: FacilityModel) {
    let managers = [];
    let supervisors = [];
    model.managers.map((m) => {
      managers.push(m._id);
    });
    model.supervisors.map((s) => {
      supervisors.push(s._id);
    });
    return this.http.put<FacilityModel>(`${this.endpoint}/facilities/${id}`, {
      ...model,
      managers,
      supervisors,
    });
  }

  addSupervisor(id: string, dto: AddSupervisorDTO) {
    return this.http.post<FacilityModel>(
      `${this.endpoint}/facilities/${id}/supervisor`,
      dto,
    );
  }

  requestChange(id: string, dto: RequestChangeDTO) {
    return this.http.post(
      `${this.endpoint}/facilities/${id}/request-change`,
      dto,
    );
  }

  createShiftTemplate(facilityId: string, dto: CreateShiftTemplateDTO) {
    return this.http.post<FacilityModel>(
      `${this.endpoint}/shift-templates/${facilityId}`,
      dto,
    );
  }

  deleteShiftTemplate(id: string) {
    return this.http.delete<FacilityModel>(
      `${this.endpoint}/shift-templates/${id}`,
    );
  }

  editShiftTemplate(id: string, dto: EditShiftTemplateDTO) {
    return this.http.put<FacilityModel>(
      `${this.endpoint}/shift-templates/${id}`,
      dto,
    );
  }

  uploadAvatar(id: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<FacilityModel>(
      `${this.endpoint}/facilities/${id}/avatar`,
      formData,
    );
  }
  changePaymentMethod(id: string, sourceToken: string) {
    return this.http.patch<FacilityModel>(
      `${this.endpoint}/facilities/${id}/payment-method`,
      { sourceToken },
    );
  }
  registerStripe(id: string) {
    return this.http.patch<FacilityModel>(
      `${this.endpoint}/facilities/${id}/register-stripe`,
      {},
    );
  }
  fetchFacilityStats(id: string) {
    return this.http.get<FacilityStats>(`${this.endpoint}/facilities/${id}/stats`);
  }
  fetchFacilityStripe(id: string) {
    return this.http.get(`${this.endpoint}/facilities/${id}/stripe`);
  }

  fetchNursesGeoLocation(facilityId: string, nurseIds: string[]) {
    let params = new HttpParams();
    nurseIds.forEach(nurseId => {
      params = params.append('nurse', nurseId);
    });
    return this.http.get<ApplicantEtaInfo[]>(`${this.endpoint}/fm-api/facilities/${facilityId}/nurses-geo-location`, { params });
  }

  fetchFacilityHistory(id: string, sort: SortDTO, dateFrom?, dateTo?, csv?, tab?, fieldsChanged?, pagination: PaginationDTO = { skip: 0, limit: HISTORY_PAGE_SIZE }) {
    let params = new HttpParams({});

    params = new HttpParams({
      fromObject: {
        ...parametrify(pagination),
        ...parametrify(sort),
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
        ...(csv ? { csv } : {}),
        ...(tab ? { tab } : {}),
        ...(fieldsChanged ? { fieldsChanged } : {})
      },
    });
    let resType = {};
    if (csv) {
      resType['responseType'] = 'blob';
    }
    return this.http.get(`${this.endpoint}/facilities/${id}/history`, { params, ...resType });

  }

  getInstashyftStats(id: string) {
    return this.http.get<InstashyftStatDTO>(`${this.endpoint}/facilities/${id}/instashyft-stats`);
  }
  putInstashyftMessage(id: string, instaShyftDefault: boolean, doNotShowInstaShyftMessage: boolean) {
    return this.http.put(
      `${this.endpoint}/facilities/${id}/show-instashyft-message`,
      { doNotShowInstaShyftMessage, instaShyftDefault },
    );
  }
  fetchFacilityRateChangeHistory(id: string) {
    return this.http.get<FacilityRateChangeHistory[]>(`${this.endpoint}/facilities/${id}/rate-change-history`);
  }
  fetchFacilityNurses(id: string, sort: SortDTO, filters: NursesFiltersDTO, pagination: PaginationDTO = { skip: 0, limit: NURSES_PAGE_SIZE }) {
    let params = new HttpParams({});
    params = new HttpParams({
      fromObject: {
        ...parametrify(sort),
        ...parametrify(filters),
        ...parametrify(pagination),
      },
    });
    return this.http.get<FacilityNurse[]>(`${this.endpoint}/fm-api/facilities/${id}/nurses`, { params, observe: 'response' }).pipe(
      map(response => ({
        models: (response.body['models'] ? response.body['models'] : response.body),
        totalCount: parseInt(response.headers.get('X-Total-Count'))
      }))
    );
  }
  fetchFacilityNurse(facilityId: string, nurseId: string) {
    return this.http.get<FacilityNurse>(`${this.endpoint}/fm-api/facilities/${facilityId}/nurses/${nurseId}`).pipe(map(response => ({
      model: response
    })));
  }
  changeFacilityNurseTrustedStatus(facilityId: string, nurseId: string, isTrusted: boolean, brokenTrustReasons: string[] = []) {
    return this.http.put<FacilityNurse>(`${this.endpoint}/fm-api/facilities/${facilityId}/nurses/${nurseId}/is-trusted`, {
      isTrusted,
      ...(brokenTrustReasons.length ? { brokenTrustReasons } : {})
    }).pipe(map(response => ({
      success: true
    })))
  }
  fetchManagerSettings(facilityId: string) {
    return this.http.get<ManagerSettings>(`${this.endpoint}/fm-api/facilities/${facilityId}/managers/me`);
  }
  setManagerSettingsTrustedConfirmationOn(facilityId: string) {
    return this.http.patch(`${this.endpoint}/fm-api/facilities/${facilityId}/managers/me`, {
      "settings": {
        "isSetNurseAsTrustedConfirmationOn": true
      }
    });
  }
  fetchFacilityNurseNotes(facilityId: string, nurseId: string) {
    return this.http.get<FacilityNurseNotes[]>(`${this.endpoint}/fm-api/facilities/${facilityId}/nurses/${nurseId}/notes`);
  }
  fetchMixedNotesHistory(facilityId: string, nurseId: string) {
    const params = new HttpParams({
      fromObject: {
        ...parametrify({
          skip: 0,
          limit: 500
        }),
      }
    });
    return this.http.get<FacilityNurseNotesMixedHistory[]>(`${this.endpoint}/fm-api/facilities/${facilityId}/nurses/${nurseId}/mixed-notes-history`, { params });
  }
  fetchMixedNotes(facilityId: string, nurseId: string) {
    const params = new HttpParams({
      fromObject: {
        ...parametrify({
          skip: 0,
          limit: 500
        }),
      }
    });
    return this.http.get<FacilityNurseMixedNotes[]>(`${this.endpoint}/fm-api/facilities/${facilityId}/nurses/${nurseId}/mixed-notes`, { params });
  }
  addFacilityNurseNotes(facilityId: string, nurseId: string, text: string) {
    return this.http.post<FacilityNurseNotes>(`${this.endpoint}/fm-api/facilities/${facilityId}/nurses/${nurseId}/notes`, { text, entityType: "facility-nurse" });
  }
  editFacilityNurseNotes(facilityId: string, nurseId: string, noteId: string, text: string) {
    return this.http.patch<FacilityNurseNotes>(`${this.endpoint}/fm-api/facilities/${facilityId}/nurses/${nurseId}/notes/${noteId}`, { text });
  }
  deleteFacilityNurseNotes(facilityId: string, nurseId: string, noteId: string) {
    return this.http.delete<any>(`${this.endpoint}/fm-api/facilities/${facilityId}/nurses/${nurseId}/notes/${noteId}`);
  }
  fetchNurseShiftPrefsRequests(facilityId: string, nurseId: string) {
    return this.http.get<NurseShiftPrefsRequest[]>(`${this.endpoint}/fm-api/facilities/${facilityId}/nurses/${nurseId}/nurse-shift-prefs-requests`);
  }
  addNurseShiftPrefsRequests(facilityId: string, nurseId: string) {
    return this.http.post<NurseShiftPrefsRequest>(`${this.endpoint}/fm-api/facilities/${facilityId}/nurses/${nurseId}/nurse-shift-prefs-requests`, {});
  }
  fetchNursesConfirmedShifts(facilityId, nurseId, dateFrom, dateTo){
    const params = new HttpParams({
      fromObject: {
        ...parametrify({
          'date-from': dateFrom,
          'date-to': dateTo
        }),
      }
    });
    return this.http.get<NursesConfirmedShift[]>(`${this.endpoint}/fm-api/facilities/${facilityId}/nurses/${nurseId}/schedule`, { params });

  }

  fetchMetricsAvailableNurses(facilityId: string) {
    const params = new HttpParams().set('radiusInMiles', 60);
    return this.http.get<AvailableNurses>(`${this.endpoint}/fm-api/facilities/${facilityId}/metrics/available-nurses`, { params });
  }

  fetchMetricsCancellationRate(facilityId: string) {
    return this.http.get<CancellationRate>(`${this.endpoint}/fm-api/facilities/${facilityId}/metrics/cancellation-rate`).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of({ cancellationRate: 0 });
        }
        throw error;
      })
    );
  }

    fetchMetricsShiftFillRate(facilityId: string) {
    return this.http.get<ShiftFillRate>(`${this.endpoint}/fm-api/facilities/${facilityId}/metrics/fill-rate`).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of({ fillRate: 0});
        }
        throw error;
      })
    );
  }

  fetchMetricsSubscriptionTier(facilityId: string) {
    return this.http.get<SubscriptionTier>(`${this.endpoint}/fm-api/facilities/${facilityId}/metrics/current-billing`);
  }

  fetchMetricsTotalSavings(facilityId: string) {
    return this.http.get<TotalSavings>(`${this.endpoint}/fm-api/facilities/${facilityId}/metrics/savings`);
  }

  fetchMetricsYourWorkforce(facilityId: string) {
    return this.http.get<YourWorkforce[]>(`${this.endpoint}/fm-api/facilities/${facilityId}/metrics/your-workforce`);
  }
}
